import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { ALL_FEATURE_FLAGS, pageFlagForLink, pageFlagForPath } from "../config/featureRegistry";

const defaultFlags = Object.fromEntries(ALL_FEATURE_FLAGS.map((item) => [item.key, true]));
const FeatureFlagsContext = createContext(null);

export function FeatureFlagsProvider({ children }) {
  const [flags, setFlags] = useState(defaultFlags);
  const [ready, setReady] = useState(false);

  const applyRows = useCallback((rows) => {
    const next = { ...defaultFlags };
    (rows || []).forEach((row) => {
      if (row?.key) next[row.key] = row.enabled !== false;
    });
    setFlags(next);
    setReady(true);
  }, []);

  const reload = useCallback(async () => {
    const { data, error } = await supabase
      .from("site_feature_flags")
      .select("key, enabled");

    if (error) {
      console.warn("Unable to load site feature flags; defaulting to enabled:", error.message || error);
      applyRows([]);
      return;
    }
    applyRows(data || []);
  }, [applyRows]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data, error } = await supabase.from("site_feature_flags").select("key, enabled");
      if (!active) return;
      if (error) {
        console.warn("Unable to load site feature flags; defaulting to enabled:", error.message || error);
        applyRows([]);
      } else {
        applyRows(data || []);
      }
    };
    void load();

    const channel = supabase
      .channel("ruht-site-feature-flags")
      .on("postgres_changes", { event: "*", schema: "public", table: "site_feature_flags" }, () => void reload())
      .subscribe();

    const onLocalChange = () => void reload();
    window.addEventListener("ruht:feature-flags-changed", onLocalChange);
    return () => {
      active = false;
      window.removeEventListener("ruht:feature-flags-changed", onLocalChange);
      supabase.removeChannel(channel);
    };
  }, [applyRows, reload]);

  const value = useMemo(() => ({
    flags,
    ready,
    isEnabled: (key) => !key || flags[key] !== false,
    isRouteEnabled: (to) => {
      const key = pageFlagForLink(to);
      return !key || flags[key] !== false;
    },
    flagForPath: pageFlagForPath,
    reload,
  }), [flags, ready, reload]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlags() {
  const value = useContext(FeatureFlagsContext);
  if (!value) {
    return {
      flags: defaultFlags,
      ready: true,
      isEnabled: () => true,
      isRouteEnabled: () => true,
      flagForPath: pageFlagForPath,
      reload: async () => {},
    };
  }
  return value;
}

export function FeatureGate({ flag, children, fallback = null }) {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag) ? children : fallback;
}
